import {
  getAmenitiesByGroupId,
  deleteAmenityByGroupId,
} from "../../../../Redux/Groups/GroupsActions";
import ConfirmationModal from "../../../Common/ConfirmationModal/ConfirmationModal";
import { getBuildingsByGroupId } from "../../../../Redux/Buildings/BuildingActions";
import { AppDispatch, RootState } from "../../../../Redux/store";
import { Menu, MenuButton, MenuItem } from "@szhsin/react-menu";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import AddAmenity from "../../Groups/AddAmenity/AddAmenity";
import toastHelper from "../../../../Util/toastHelper";
import { useDispatch, useSelector } from "react-redux";
import EditAmenity from "../EditAmenity/EditAmenity";
import { GridReadyEvent } from "ag-grid-community";
import React, { useEffect, useMemo, useState } from "react";
import Modal from "../../../Common/Modal/Modal";
import { HiDotsVertical } from "react-icons/hi";
import styles from "./Amenities.module.scss";
import { AgGridReact } from "ag-grid-react";
import { FaSearch } from "react-icons/fa";

interface AmenitiesType {
  amenityId: number;
  amenityTypeId: number;
  amenityTypeName: string;
  image: string;
  lockId: string;
  name: string;
  rate: number;
  rateTypeId: number;
  rateTypeName: string;
  rules: string;
  status: string;
  requiresBooking: boolean;
}

const defaultColDef: ColDef = {
  resizable: true,
  sortable: true,
  filter: false,
  wrapText: true,
  autoHeight: true,
  unSortIcon: true,
};

interface Props {
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
  groupId: string;
}

function Amenities(props: Props) {
  const { setActiveTab, groupId } = props;
  const [rowData, setRowData] = useState<AmenitiesType[]>([]);
  const [gridApi, setGridApi] = useState<any>(null);
  const [addAmenity, setAddAmenity] = useState(false);
  const [editAmenityModal, setEditAmenityModal] = useState(false);
  const [amenityData, setAmenityData] = useState<any>(null);
  const dispatch = useDispatch<AppDispatch>();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteAmenityId, setDeleteAmenityId] = useState<number>(-1);
  const { getAmenitiesByGroupIdData, deleteAmenityByGroupIdLoading } =
    useSelector((state: RootState) => state.groups);
  const [search, setSearch] = useState("");
  const columnDefs: ColDef<AmenitiesType>[] = [
    {
      headerName: "Amenity Name",
      field: "name",
      flex: 1,
      minWidth: 230,
    },

    {
      headerName: "Amenity Type",
      field: "amenityTypeName",
      flex: 1,
      minWidth: 200,
    },

    {
      headerName: "Rate Type",
      field: "rateTypeName",
      flex: 1,
      minWidth: 150,
    },

    {
      headerName: "Requires Booking",
      field: "requiresBooking",
      flex: 1,
      minWidth: 150,
    },

    {
      headerName: "Rates",
      valueGetter: (params) => {
        return `$ ${params?.data?.rate}`;
      },
      flex: 1,
      minWidth: 100,
    },

    {
      headerName: "Status",
      field: "status",
      flex: 1,
      minWidth: 100,
      cellClass: (params) => {
        return params?.data?.status === "Inactive"
          ? "pendingText"
          : params?.data?.status === "Active"
            ? "CompletedText"
            : "text-black-400";
      },
    },
    {
      colId: "actions",
      sortable: false,
      suppressAutoSize: true,
      suppressMovable: true,
      suppressNavigable: true,
      editable: false,
      cellStyle: {
        pointerEvents: "all",
        userSelect: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
      },
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <div className={styles.iconContainer}>
            <Menu
              direction="left"
              menuButton={
                <MenuButton>
                  <HiDotsVertical className={styles.icon} />
                </MenuButton>
              }
              transition
              portal
            >
              <MenuItem
                onClick={() => {
                  setEditAmenityModal(true);
                  setAmenityData(params?.data);
                }}
              >
                Edit
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setIsDeleteModalOpen(true);
                  setDeleteAmenityId(params?.data?.amenityId);
                }}
              >
                Delete
              </MenuItem>
            </Menu>
          </div>
        );
      },
    },
  ];

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    params.api.sizeColumnsToFit();
  };

  useEffect(() => {
    if (!gridApi) return;

    const handleResize = () => {
      gridApi.sizeColumnsToFit();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [gridApi]);

  useEffect(() => {
    if (getAmenitiesByGroupIdData) {
      setRowData(getAmenitiesByGroupIdData);
    }
  }, [getAmenitiesByGroupIdData]);

  useEffect(() => {
    if (groupId) {
      dispatch(
        getAmenitiesByGroupId({
          groupId,
        }),
      );
      dispatch(
        getBuildingsByGroupId({
          groupId,
        }),
      );
    }
  }, [dispatch, groupId]);

  const nextHandler = () => {
    if (
      Array.isArray(getAmenitiesByGroupIdData) &&
      getAmenitiesByGroupIdData.length > 0
    ) {
      setActiveTab("packages");
    } else {
      toastHelper.error("Please add at least one amenity");
    }
  };

  const deleteAmenityConfirmationHandler = () => {
    const successHandler = () => {
      setIsDeleteModalOpen(false);
      setDeleteAmenityId(-1);
    };
    dispatch(
      deleteAmenityByGroupId({
        id: deleteAmenityId,
        groupId,
        successHandler,
      }),
    );
  };

  const filteredRowData = useMemo(() => {
    return rowData.filter((item) =>
      item.name.toLowerCase().includes(search.toLowerCase()),
    );
  }, [rowData, search]);

  return (
    <React.Fragment>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        onConfirm={deleteAmenityConfirmationHandler}
        heading="Delete Amenity"
        text="Are you sure you want to delete this amenity?"
        loading={deleteAmenityByGroupIdLoading}
      />
      <Modal
        isOpen={addAmenity}
        setIsOpen={setAddAmenity}
        showCloseIcon={false}
      >
        <AddAmenity
          isOpen={addAmenity}
          setIsOpen={setAddAmenity}
          groupId={groupId}
        />
      </Modal>

      <Modal
        isOpen={editAmenityModal}
        setIsOpen={setEditAmenityModal}
        showCloseIcon={false}
      >
        <EditAmenity
          isOpen={editAmenityModal}
          setIsOpen={setEditAmenityModal}
          groupId={groupId}
          amenityData={amenityData}
        />
      </Modal>
      <div className={styles.mainContainer}>
        <div className="topContainer">
          <div className={styles.searchContainer}>
            <input
              type="text"
              placeholder="Search Amenity"
              className={styles.input}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <FaSearch className={styles.icon} />
          </div>
        </div>
        <div className={styles.gridContainer}>
          <AgGridReact
            className={styles.grid}
            rowData={filteredRowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={[5, 10, 20, 50, 100]}
            animateRows={true}
            domLayout="normal"
          />
        </div>
        <div className={styles.buttonContainer}>
          <button className={styles.button} onClick={() => setAddAmenity(true)}>
            Add Amenity
          </button>
        </div>
        <div className={styles.buttonContainer}>
          <button className={styles.nextButton} onClick={nextHandler}>
            Next
          </button>
          <button
            className={styles.backButton}
            onClick={() => setActiveTab("groupInformation")}
          >
            Back
          </button>
        </div>
      </div>
    </React.Fragment>
  );
}

export default Amenities;
